import { Row } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PanelCollapse from '../../../../../../../shared/components/collapse/PanelCollapse';
import { FormSelect } from '../../../../../../../shared/components/crmFormElement/FormInput';
import { GUTTOR_SPACE } from '../../../../../../../shared/constants/commonConstants';
import { CATARACT_DIAGNOSIS_TYPE, CATARACT_LENS_TYPE, CATARACT_TYPE, DIALYSIS_DIAGNOSIS_TYPE } from '../../../../../../../shared/constants/crmConstants';
import { useFindPayer } from '../../../../../../../shared/hooks/useFindPayer';
import { useForceUpdate } from '../../../../../../../shared/hooks/useForceUpdate';
import { useSetFormControlConfig } from '../../../../../../../shared/hooks/useSetFormControlConfig';
import { RootState } from '../../../../../../../store';
import { dialysisValidation, getIsPageNameDetail, getSelectedDiagnosisIds } from '../../../../service/crmUtils';
import CONTROL_CONFIG_BASED_ON_PAYER from '../../../../../../../config/formControlConfig';
import { intersection } from 'lodash';
import FormInputNumber from '../../../../../../../shared/components/crmFormElement/FormInputNumber/FormInputNumber';
import { NewAdmissionState } from '../../../../../../../store/models/newAdmissionModel';

const OtherTreatmentDetailCard = ({ form, isLoading, location: { pathname } }: any): ReactElement | null => {
  const [claimDetailBackup, setClaimDetailBackup] = useState<any>({});
  const [activeKey, setActiveKey] = useState<number[]>([]);
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState<any[]>([]);
  const {
    payerLookUp = {}, payerList: { payer: { uniqueIdentifier, name } },
    newAdmission: { selectedBeneficiaryDetail = {}, cardCollapse: { isOtherTreatmentDetailOpen, selectedDiagnosisIds },
    },
    admissionDetails: { claimDetails: claimDetail = {} },
  } = useSelector((state: RootState): RootState => state);
  const { isManualSubmission } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  let payerName = name;
  const findPayer = useFindPayer();
  if (claimDetail && !payerName) {
    const { payerId } = claimDetail;
    payerName = findPayer(payerId);
  }

  const [cataractTypes, setCataractTypes] = useState([] as any);
  const { memberAge }: any = selectedBeneficiaryDetail || {};
  const { patientAge } = claimDetail;

  const payerConfig = CONTROL_CONFIG_BASED_ON_PAYER[payerName];

  let formElementCount: any[] = [];
  if (payerConfig) {
    const payerConfigValues = Object.values(payerConfig);
    formElementCount = payerConfigValues.filter(({ type, hidden }: any): boolean => type === 'treatment' && !hidden);
  }

  const forceUpdate = useForceUpdate();
  const getValueBasedOnConfig = useSetFormControlConfig();

  useEffect((): void => {
    if (memberAge || patientAge || memberAge >= 0 || patientAge >= 0 || (memberAge === undefined && patientAge === undefined)) {
      let age: number;
      if (patientAge >= 0) {
        age = patientAge;
      }
      if (memberAge >= 0) {
        age = memberAge;
      }
      if (isManualSubmission) {
        age = 45;
      }
      const types = CATARACT_TYPE
        .filter(({ ageStartLimit, ageEndLimit }: any): any =>
          ageStartLimit <= age && ageEndLimit >= age)
        .map((type: any): any => ({ ...type, value: type.id }));
      setCataractTypes(types);
    }
  }, [memberAge, patientAge]);

  useEffect((): void => {
    if (claimDetail && Object.keys(claimDetail).length) {
      const isPageNameDetail = getIsPageNameDetail(pathname);
      const { cataract: { ailmentid: cataractType, lenseId: contactLensType }, dialysis } = claimDetail;
      let formValue = {};

      if (dialysis?.dialysis) {
        formValue = {
          dialysis: dialysis?.dialysis,
        };
      }
      if (cataractType) {
        formValue = {
          ...formValue,
          cataractType: isPageNameDetail ? cataractType : parseInt(cataractType, 10),
        };
      }
      if (contactLensType) {
        formValue = {
          ...formValue,
          contactLensType: isPageNameDetail ? contactLensType : parseInt(contactLensType, 10),
        };
      }

      const updatedFormValues = getValueBasedOnConfig(isPageNameDetail, formValue);
      setClaimDetailBackup(updatedFormValues);
      form.setFieldsValue(updatedFormValues);
      forceUpdate();
    }
  }, [claimDetail]);

  useEffect((): void => {
    if (typeof isOtherTreatmentDetailOpen === 'boolean') {
      if (isOtherTreatmentDetailOpen) {
        if (!getIsPageNameDetail(pathname)) {
          const diagnosisIds = form.getFieldsValue(['diagnosisMandatory'])?.diagnosisMandatory !== 'Other'
          ? [form.getFieldsValue(['diagnosisMandatory'])?.diagnosisMandatory] : form.getFieldsValue(['diagnosis'])?.diagnosis;
          setSelectedDiagnosisId(getSelectedDiagnosisIds(diagnosisIds, payerLookUp[uniqueIdentifier]?.diagnosis));
        } else {
          const { originalDiagnosis } = claimDetail;
          const diagnosisIds = originalDiagnosis.map(({ payerMasterId }: any): string => (payerMasterId || '').toString());
          setSelectedDiagnosisId(diagnosisIds);
        }
        setActiveKey([1]);
      } else {
        setActiveKey([]);
      }
    }
  }, [isOtherTreatmentDetailOpen, selectedDiagnosisIds]);

  const commonDetail = { isLoading, form, claimDetailBackup };
  const [cataract1, cataract2] = CATARACT_DIAGNOSIS_TYPE;
  const [dialysisCode] = DIALYSIS_DIAGNOSIS_TYPE;

  const hasShowFormElement = (diagnosisId: string[]): boolean =>  !intersection(selectedDiagnosisId, diagnosisId).length;

  return (
    formElementCount?.length ? <PanelCollapse accordion={true} expandIconPosition="right"
    activeKey={activeKey} keyName="1" header="Other Details">
      <Row gutter={GUTTOR_SPACE} data-testid="other-treatment-detail">
        <FormSelect detail={{
          ...commonDetail,
          key: 'cataractType',
          options: cataractTypes,
          size: 12,
          labelClass: 'mb-0',
          required: isOtherTreatmentDetailOpen,
          hidden: hasShowFormElement([cataract1, cataract2]),
        }} />
        <FormSelect detail={{
          ...commonDetail,
          key: 'contactLensType',
          options: [...CATARACT_LENS_TYPE],
          size: 12,
          labelClass: 'mb-0',
          required: isOtherTreatmentDetailOpen,
          hidden: hasShowFormElement([cataract1, cataract2]),
        }} />
        <FormInputNumber detail={{
          ...commonDetail,
          key: 'dialysis',
          size: 12,
          required: isOtherTreatmentDetailOpen,
          hidden: hasShowFormElement([dialysisCode]),
          noOfDecimalAllowed: 0,
          rules: [dialysisValidation],
        }}
        />
      </Row>
    </PanelCollapse> : null
  );
};

export default withRouter(OtherTreatmentDetailCard);
